import { AADetectionResults } from "../types";
import {
  getInteractionCountData,
  getLastRenderDate,
  incrementInteractionCount,
} from "../data";
import { LocalSettings, WallMode } from "../../settings";
import { getUserCountry } from "../data/userCountry";

/**
 * renderAllowed checks if readership link could be rendered by next rules:
 * - RL feature is enabled and script run on supported device
 * - Consent message wasn't shown in current page view and all required consents given
 * - PV render count data met requirement based on the page_views_to_render setting
 * - Country code included (or not excluded) for rendering
 * - The render days interval met requirement based on render_interval_days setting
 * @param {LocalSettings} settings - the readership link local settings
 * @returns {boolean} - if rendering allowed based on settings
 * */
export async function renderAllowed(settings: LocalSettings): Promise<boolean> {
  if (!settings.enabled || isMobile()) return false;

  const locationURL = window.location.href;
  if (settings.allowlisted_urls?.some((url) => locationURL.startsWith(url))) {
    return false;
  }

  const [lastRenderDate, renderCount, dismissCount] = await Promise.all([
    getLastRenderDate(),
    getInteractionCountData("render"),
    getInteractionCountData("dismiss"),
  ]);

  // frequency setting check
  const isWithinRenderInterval =
    lastRenderDate &&
    differenceInDays(new Date(), lastRenderDate) <
      settings.render_interval_days;
  if (isWithinRenderInterval) return false;

  const renderAllowedByGeo = await renderAllowedByGeolocation(settings);
  if (!renderAllowedByGeo) {
    return false;
  }

  if (settings?.wall_mode === WallMode.dynamic) {
    const renderAllowed = renderAllowedForDynamicWall(
      settings.dynamic_wall_settings,
      renderCount,
      dismissCount,
    );
    if (!renderAllowed) {
      return false;
    }
  } else {
    // we don't expect this setting if dynamic wall enabled
    if (renderCount < settings.page_views_to_render) {
      incrementInteractionCount("render");
      return false;
    }
  }

  return true;
}

export const adsAllowed = (
  aaDetectionResults: AADetectionResults,
  renderToAAUsers?: boolean,
): boolean => {
  return (
    aaDetectionResults &&
    (!aaDetectionResults.ab || (aaDetectionResults.aa && !renderToAAUsers))
  );
};

const differenceInDays = (firstDate: Date, secondDate: Date): number => {
  const diff = firstDate.getTime() - secondDate.getTime();
  return Math.round(diff / (1000 * 3600 * 24));
};

/**
 * whether browser is mobile or not (includes tablets)
 */
export function isMobile() {
  const userAgent = (navigator.userAgent ||
    navigator.vendor ||
    window.opera) as string | undefined;
  if (!userAgent) return false;

  return (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
      userAgent,
    ) ||
    // eslint-disable-next-line no-useless-escape
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      userAgent.substr(0, 4),
    )
  );
}

/**
 * verify country-based rendering conditions
 * */
async function renderAllowedByGeolocation(
  settings: LocalSettings,
): Promise<boolean> {
  // if country code list is not defined or empty we going to ignore country filtering
  if (!settings.country_code_list || settings.country_code_list.length <= 0) {
    return true;
  }

  const userCountry = await getUserCountry();
  window.__bt_intrnl.country = userCountry;

  if (!userCountry) return false;

  const userCountryFoundInTheList = settings.country_code_list.includes(
    userCountry.toUpperCase(),
  );

  const renderNotAllowed =
    (userCountryFoundInTheList && settings.country_code_mode === "excluding") ||
    (!userCountryFoundInTheList && settings.country_code_mode === "including");

  return !renderNotAllowed;
}

/**
 * verify render allowed for dynamic wall mode
 * */
function renderAllowedForDynamicWall(
  dynamic_wall_settings: { pageviews_count: number },
  renderCount: number,
  dismissCount: number,
): boolean {
  // First render init the modal
  if (dismissCount === 0 && renderCount === 0) {
    return true;
  }

  if (renderCount < dynamic_wall_settings.pageviews_count) {
    incrementInteractionCount("render");
    return false;
  }
  return true;
}
